import { CSSProperties } from 'react';
import { BreakpointsOptions, createBreakpoints } from '@mui/system';
import tokens from '@skodaflow/web-tokens/src/tokens.json';

export type TypographySize = 'small' | 'medium' | 'large';

/**
 * Obtain style for element for the certain size
 */
export const getTypography = (element: string, rtl: boolean, size?: TypographySize) => {
  const screen = size ?? 'small';
  const typo = tokens['flow-typography'].find((el) => el.name === `${screen}-${element}`);
  if (typo)
    return {
      ...typo['style'],
      // replace font for rtl variant
      ...(rtl
        ? {
            fontFamily: 'SKODA Next R2L',
          }
        : {}),
    } as CSSProperties;
  else return null;
};

/**
 * Generate responsive typography style with the media queries
 */
export const generateTypography = (element: string, breakpointsOptions: BreakpointsOptions, rtl: boolean) => {
  const breakpoints = createBreakpoints(breakpointsOptions);

  return {
    ...getTypography(element, rtl, 'small'),
    [breakpoints.up('md')]: {
      ...getTypography(element, rtl, 'medium'),
    },
    [breakpoints.up('xl')]: {
      ...getTypography(element, rtl, 'large'),
    },
  };
};

/**
 * Build the typography theme options
 */
export const getTypographyOptions = (breakpointsOptions: BreakpointsOptions, rtl: boolean) => {
  const bodyTypo = generateTypography('body', breakpointsOptions, rtl);

  return {
    fontFamily: [`SKODA Next${rtl ? ' R2L' : ''}`, 'sans-serif', 'Arial'].join(','),
    body: bodyTypo,
    ...Object.fromEntries(
      [
        'h1',
        'h2',
        'h3',
        'h4',
        'subheadline',
        'subtitle1',
        'subtitle2',
        'display',
        'list',
        'disclaimer',
        'disclaimer1',
        'disclaimer2',
        'caption1',
        'caption2',
      ].map((element) => [element, generateTypography(element, breakpointsOptions, rtl)]),
    ),
    // original @mui properties
    body1: bodyTypo,
    body2: bodyTypo,
    h5: generateTypography('subheadline', breakpointsOptions, rtl),
    h6: generateTypography('caption1', breakpointsOptions, rtl),
    caption: generateTypography('caption2', breakpointsOptions, rtl),
    overline: generateTypography('caption1', breakpointsOptions, rtl),
    // button, later this definition should come from @skodaflow/web-tokens
    button: {
      fontFamily: rtl ? 'SKODA Next R2L' : 'SKODA Next',
      letterSpacing: '0.75px',
      textTransform: 'none',
      textDecoration: 'none',
      fontSize: '16px',
      fontWeight: '700',
      lineHeight: '16px',
    },
  };
};
